import { useState } from 'react'
import { useCreateIntegracao, useUpdateIntegracao } from '@/hooks/useIntegracoes'
import { X, Loader2, Save, Code, Globe, Shield } from 'lucide-react'
import type { Integracao, IntegracaoTipo, IntegracaoAmbiente, HttpMethod } from '@/types/database'

interface IntegracaoFormProps {
    integracao: Integracao | null
    onClose: () => void
    onSuccess: () => void
}

export function IntegracaoForm({ integracao, onClose, onSuccess }: IntegracaoFormProps) {
    const createMut = useCreateIntegracao()
    const updateMut = useUpdateIntegracao()
    const isPending = createMut.isPending || updateMut.isPending

    // Basic Info
    const [nome, setNome] = useState(integracao?.nome || '')
    const [tipo, setTipo] = useState<IntegracaoTipo>(integracao?.tipo || 'rest')
    const [ambiente, setAmbiente] = useState<IntegracaoAmbiente>(integracao?.ambiente || 'sandbox')
    const [ativo, setAtivo] = useState(integracao?.ativo ?? true)

    // Request Config
    const [endpoint, setEndpoint] = useState(integracao?.endpoint || '')
    const [method, setMethod] = useState<HttpMethod>(integracao?.method || 'POST')

    // JSON Configs (stored as string for editing)
    const [headers, setHeaders] = useState(JSON.stringify(integracao?.headers || {}, null, 2))
    const [authConfig, setAuthConfig] = useState(JSON.stringify(integracao?.auth_config || {}, null, 2))
    const [bodyTemplate, setBodyTemplate] = useState(JSON.stringify(integracao?.body_template || {}, null, 2))

    // Resilience
    const [timeoutMs, setTimeoutMs] = useState(String(integracao?.timeout_ms || '5000'))
    const [retryMax, setRetryMax] = useState(String(integracao?.retry_max || '3'))

    const [tab, setTab] = useState<'geral' | 'auth' | 'body'>('geral')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            // Validate JSONs
            const headersJson = JSON.parse(headers)
            const authConfigJson = JSON.parse(authConfig)
            const bodyTemplateJson = JSON.parse(bodyTemplate)

            const payload = {
                nome,
                tipo,
                ambiente,
                endpoint,
                method,
                headers: headersJson,
                auth_config: authConfigJson,
                body_template: bodyTemplateJson,
                timeout_ms: parseInt(timeoutMs),
                retry_max: parseInt(retryMax),
                ativo,
                // Default values
                parametros: {},
                retry_delay_ms: 1000,
                eventos_trigger: [],
                servico_id: null,
                versao_atual: (integracao?.versao_atual || 1) + 1
            }

            if (integracao) {
                await updateMut.mutateAsync({ id: integracao.id, ...payload })
            } else {
                await createMut.mutateAsync(payload)
            }
            onSuccess()
        } catch (err) {
            alert('Erro ao salvar: Verifique se os campos JSON são válidos.')
            console.error(err)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden rounded-3xl shadow-elevated">
                {/* Header */}
                <div className="p-4 border-b border-surface-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-surface-900">
                        {integracao ? `Editar ${integracao.nome}` : 'Nova Integração'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-600">
                        <X size={20} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto p-0 flex flex-col md:flex-row">
                    {/* Sidebar Tabs */}
                    <div className="w-full md:w-64 bg-white p-4 border-r border-surface-100 flex flex-row md:flex-col gap-2 overflow-x-auto shrink-0">
                        <button type="button" onClick={() => setTab('geral')}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left
                            ${tab === 'geral' ? 'bg-primary-50 text-primary-600 ring-1 ring-primary-100' : 'text-surface-600 hover:bg-surface-50'}`}>
                            <Globe size={18} /> Dados Gerais
                        </button>
                        <button type="button" onClick={() => setTab('auth')}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left
                            ${tab === 'auth' ? 'bg-primary-50 text-primary-600 ring-1 ring-primary-100' : 'text-surface-600 hover:bg-surface-50'}`}>
                            <Shield size={18} /> Autenticação
                        </button>
                        <button type="button" onClick={() => setTab('body')}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all text-left
                            ${tab === 'body' ? 'bg-primary-50 text-primary-600 ring-1 ring-primary-100' : 'text-surface-600 hover:bg-surface-50'}`}>
                            <Code size={18} /> Body & Template
                        </button>
                    </div>

                    {/* Content Form */}
                    <form id="integracao-form" onSubmit={handleSubmit} className="flex-1 p-6 space-y-6">

                        {/* TAB: GERAL */}
                        {tab === 'geral' && (
                            <div className="space-y-6 animate-fade-in">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="label">Nome da Integração</label>
                                        <input type="text" required value={nome} onChange={e => setNome(e.target.value)}
                                            className="input" placeholder="Ex: Rastreamento XYZ" />
                                    </div>

                                    <div>
                                        <label className="label">Tipo</label>
                                        <select value={tipo} onChange={e => setTipo(e.target.value as any)} className="input">
                                            <option value="rest">REST API</option>
                                            <option value="webhook">Webhook</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="label">Ambiente</label>
                                        <select value={ambiente} onChange={e => setAmbiente(e.target.value as any)} className="input">
                                            <option value="sandbox">Sandbox (Teste)</option>
                                            <option value="producao">Produção</option>
                                        </select>
                                    </div>

                                    <div className="col-span-2">
                                        <label className="label">Endpoint URL</label>
                                        <div className="flex">
                                            <select value={method} onChange={e => setMethod(e.target.value as any)}
                                                className="input w-28 rounded-r-none border-r-0 bg-white font-mono text-xs font-bold">
                                                <option value="GET">GET</option>
                                                <option value="POST">POST</option>
                                                <option value="PUT">PUT</option>
                                                <option value="PATCH">PATCH</option>
                                                <option value="DELETE">DELETE</option>
                                            </select>
                                            <input type="url" required value={endpoint} onChange={e => setEndpoint(e.target.value)}
                                                className="input rounded-l-none font-mono text-sm" placeholder="https://api.parceiro.com.br/v1/..." />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="label">Timeout (ms)</label>
                                        <input type="number" required value={timeoutMs} onChange={e => setTimeoutMs(e.target.value)}
                                            className="input" />
                                    </div>

                                    <div>
                                        <label className="label">Max Retries</label>
                                        <input type="number" required value={retryMax} onChange={e => setRetryMax(e.target.value)}
                                            className="input" />
                                    </div>

                                    <div className="col-span-2 pt-2">
                                        <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-100 bg-white cursor-pointer">
                                            <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)}
                                                className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500" />
                                            <div>
                                                <span className="font-medium text-surface-900">Integração Ativa</span>
                                                <p className="text-xs text-surface-600">Se desmarcado, nenhuma chamada será realizada.</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB: AUTH */}
                        {tab === 'auth' && (
                            <div className="space-y-6 animate-fade-in">
                                <div>
                                    <label className="label flex justify-between">
                                        <span>Authentication Config (JSON)</span>
                                        <span className="text-xs text-surface-500">Credenciais, Tokens, Keys...</span>
                                    </label>
                                    <textarea value={authConfig} onChange={e => setAuthConfig(e.target.value)}
                                        className="input font-mono text-xs h-40"
                                        placeholder='{ "type": "bearer", "token": "..." }' />
                                </div>

                                <div>
                                    <label className="label flex justify-between">
                                        <span>Global Headers (JSON)</span>
                                        <span className="text-xs text-surface-500">Headers enviados em todas as requests</span>
                                    </label>
                                    <textarea value={headers} onChange={e => setHeaders(e.target.value)}
                                        className="input font-mono text-xs h-40"
                                        placeholder='{ "Content-Type": "application/json" }' />
                                </div>
                            </div>
                        )}

                        {/* TAB: BODY */}
                        {tab === 'body' && (
                            <div className="space-y-6 animate-fade-in">
                                <div className="h-full flex flex-col">
                                    <label className="label flex justify-between">
                                        <span>Body Template (JSON)</span>
                                        <span className="text-xs text-surface-500">Use variáveis como {"{{associado.nome}}"}</span>
                                    </label>
                                    <textarea value={bodyTemplate} onChange={e => setBodyTemplate(e.target.value)}
                                        className="input font-mono text-xs flex-1 min-h-[300px]"
                                        placeholder='{
    "customer": {
        "name": "{{associado.nome}}",
        "document": "{{associado.cpf}}"
    },
    "vehicle": {
        "plate": "{{veiculo.placa}}"
    }
}' />
                                    <div className="mt-2 p-3 bg-blue-50 text-blue-800 text-xs rounded-lg border border-blue-100">
                                        <strong>Variáveis disponíveis:</strong> associado.*, veiculo.*, contrato.*, servico.*
                                    </div>
                                </div>
                            </div>
                        )}

                    </form>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-surface-100 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="btn border border-surface-200 hover:bg-white text-surface-700">
                        Cancelar
                    </button>
                    <button type="submit" form="integracao-form" disabled={isPending}
                        className="btn btn-primary gap-2 shadow-lg shadow-primary-500/20">
                        {isPending ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        Salvar Integração
                    </button>
                </div>
            </div>
        </div>
    )
}
